import type { IOrg } from 'src/types/organization';
import type { IUserSummary } from 'src/types/user';
import type { IImageForUpload } from 'src/types/image';

import useSWR, { mutate } from 'swr';
import { useMemo, useState, useCallback } from 'react';

import {
  poster,
  fetcher,
  updater,
  getEndpoints,
  createEndpoints,
  updateEndpoints,
} from 'src/utils/axios';

import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

import { appendImageToFormData } from 'src/types/image';

// ----------------------------------------------------------------------

const swrOptions = {
  revalidateIfStale: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
};

interface OrgBaseData {
  data: IOrg;
}

interface UsersData {
  data: IUserSummary[];
}

export function useGetOrganizationBase() {
  const { orgGuid } = useWorkspaceContext();

  // Add guard for missing orgGuid
  const url = orgGuid ? getEndpoints.organization.base(orgGuid) : null;

  // Pass null URL when orgGuid is missing to prevent unnecessary request
  const { data, isLoading, error, isValidating } = useSWR<OrgBaseData>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      organization: data?.data || [], // Use nullish coalescing instead of logical OR
      organizationLoading: isLoading,
      organizationError: error,
      organizationValidating: isValidating,
      organizationEmpty: !isLoading && !data?.data,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export type PartialOrgUpdate = Pick<IOrg, 'name' | 'tagline' | 'bio' | 'webURL'>;

export function useUpdateOrganization(guid: string) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateOrganization = useCallback(
    async ({ name, tagline, bio, webURL }: PartialOrgUpdate) => {
      setIsUpdating(true);
      setError(null);

      try {
        const formData = new FormData();
        if (name !== undefined) formData.append('name', name);
        if (tagline !== undefined) formData.append('tagline', tagline);
        if (bio !== undefined) formData.append('bio', bio);
        if (webURL !== undefined) formData.append('webURL', webURL);
        // Send the form data to the appropriate endpoint

        const result = await mutate(
          updateEndpoints.organization.organization(guid),
          updater(updateEndpoints.organization.organization(guid), formData)
        );

        // Revalidate the data
        await mutate(getEndpoints.organization.base(guid), undefined, { revalidate: true });

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [guid]
  );

  const memoizedValue = useMemo(
    () => ({
      updateOrganization,
      isUpdating,
      error,
    }),
    [updateOrganization, isUpdating, error]
  );

  return memoizedValue;
}

export function useCreateOrganizationProfileImage(guid: string) {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrganizationProfileImage = useCallback(
    async (imageForUpload: IImageForUpload) => {
      setIsCreating(true);
      setError(null);

      try {
        const formData = new FormData();
        appendImageToFormData(formData, imageForUpload, 'primaryImage');

        // Send the form data to the appropriate endpoint
        const result = await mutate(
          createEndpoints.organization.image(guid),
          poster(createEndpoints.organization.image(guid), formData)
        );
        if (result.error) {
          throw result.error;
        }
        
        await mutate(getEndpoints.organization.base(guid), undefined, { revalidate: true });
        await mutate(
          (key) => typeof key === 'string' && key.includes(`/organization/${guid}`),
          undefined,
          { revalidate: true }
        );

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [guid]
  );

  const memoizedValue = useMemo(
    () => ({
      createOrganizationProfileImage,
      isCreating,
      error,
    }),
    [createOrganizationProfileImage, isCreating, error]
  );

  return memoizedValue;
}

export function useGetOrganizationUsers() {
  const { orgGuid } = useWorkspaceContext();
  const { data, isLoading, error } = useSWR<UsersData>(
    orgGuid ? getEndpoints.organization.users(orgGuid) : null,
    fetcher,
    swrOptions
  );
  const memoizedValue = useMemo(
    () => ({
      users: data?.data || [],
      isLoading,
      error,
    }),
    [data?.data, isLoading, error]
  );
  return memoizedValue;
}
